/** srcdoc 预览：拦截站内跳转 + 点选元素发回父页。 */

import idiomorphMin from "idiomorph/dist/idiomorph.min.js?raw";

export const BRAND_SITE_PREVIEW_MESSAGE = "c456-brand-site-preview";

/** 悬停 @n 芯片时通知预览高亮对应节点（path 为 null 则取消）。 */
export const BRAND_SITE_PICK_HOVER_EVENT = "c456:brand-site-pick-hover";

export function emitBrandSitePickHover(path: string | null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(BRAND_SITE_PICK_HOVER_EVENT, { detail: { path } }),
  );
}

export type PreviewHrefKind = "internal" | "hash" | "external" | "ignore";

export type ParsedPreviewHref =
  | { kind: "internal"; slug: string; path: string }
  | { kind: "hash" }
  | { kind: "external"; url: string }
  | { kind: "ignore" };

export type PreviewPickItem = {
  tag: string;
  text: string;
  path: string;
  html: string;
  rect: { top: number; left: number; width: number; height: number };
};

export type PreviewPickPayload = {
  source: typeof BRAND_SITE_PREVIEW_MESSAGE;
  type: "pick";
  items: PreviewPickItem[];
  activeIndex: number;
};

export function homeSlugFromPages(pages: Array<{ slug: string; is_home?: boolean }>): string {
  return pages.find((page) => page.is_home)?.slug ?? pages[0]?.slug ?? "index";
}

export function pathForPreviewSlug(slug: string, homeSlug: string): string {
  if (!slug || slug === homeSlug || slug === "index") return "/";
  return `/${slug}`;
}

/** 是否为栏目预览导航 slug：`blog` / `blog*` / `blog/:slug` / `blog/hello` */
export function isBrandSiteCollectionNavSlug(
  slug: string,
  collections: ReadonlyArray<{ key: string }>,
): boolean {
  const raw = slug.trim();
  if (!raw) return false;
  return collections.some((collection) => {
    const key = collection.key.trim();
    if (!key) return false;
    return (
      raw === key ||
      raw === `${key}*` ||
      raw === `${key}/:slug` ||
      raw.startsWith(`${key}/`)
    );
  });
}

/** 栏目列表预览请求 path：`blog*` / `blog` → `/blog`（与 brand-site preview 对齐） */
export function pathForCollectionListPreview(slug: string): string {
  const key = slug.trim().replace(/\*+$/, "");
  return key ? `/${key}` : "/";
}

/**
 * 设计预览是否调 brand-site preview API（展开 Liquid / 栏目模板）。
 * 静态正文页草稿常含 `{% render %}`，不能直接塞 html_content，否则源码露出。
 * 规则：有账户与站点时，除 `_` 公共部分外一律走 preview。
 */
export function shouldUseBrandSitePreviewApi(input: {
  hasAccountAndSite: boolean;
  viewingSlug: string;
  hasViewingPage: boolean;
  collectionRoute: boolean;
  viewingNodeKind?: string | null;
}): boolean {
  if (!input.hasAccountAndSite) return false;
  const slug = input.viewingSlug.trim();
  if (slug.startsWith("_")) return false;
  return true;
}

/** 草稿里是否仍含未展开的 Liquid（预览绝不该直接展示这些） */
export function htmlLooksLikeLiquidSource(html: string): boolean {
  return /\{%\s*(render|include|assign|if|for|schema|capture)\b/i.test(html);
}

export function slugFromPreviewPath(path: string, homeSlug: string): string {
  const trimmed = path.trim() || "/";
  const pathname = trimmed.split("#")[0]?.split("?")[0] ?? "/";
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/" || normalized === "/index" || normalized === "/index.html") {
    return homeSlug;
  }
  const last = normalized.slice(1).replace(/\.html$/i, "");
  return last || homeSlug;
}

export function parsePreviewHref(href: string, homeSlug: string): ParsedPreviewHref {
  const raw = href.trim();
  if (!raw || raw.toLowerCase().startsWith("javascript:")) return { kind: "ignore" };
  if (raw.startsWith("#")) return { kind: "hash" };
  if (/^(mailto:|tel:)/i.test(raw)) return { kind: "ignore" };

  try {
    const url = new URL(raw, "https://preview.local/");
    if (url.protocol === "http:" || url.protocol === "https:") {
      if (url.hostname !== "preview.local") {
        return { kind: "external", url: url.toString() };
      }
    } else if (url.protocol !== "https:") {
      return { kind: "ignore" };
    }
    const path = `${url.pathname}${url.search}` || "/";
    const slug = slugFromPreviewPath(url.pathname, homeSlug);
    return { kind: "internal", slug, path: path.replace(/\/index(?:\.html)?$/i, "") || "/" };
  } catch {
    return { kind: "ignore" };
  }
}

const CLICK_HANDLER =
  "(function(e){if(window.__c456PickOn)return;var t=e.target;if(t&&t.nodeType===3)t=t.parentElement;var a=t&&t.closest?t.closest('a'):null;if(!a)return;var h=a.getAttribute('href');if(!h||h.charAt(0)==='#')return;e.preventDefault();try{parent.postMessage({source:'c456-brand-site-preview',type:'nav',href:h},'*')}catch(x){}})(event)";

const SUBMIT_HANDLER = "event.preventDefault();return false;";

/** 点选运行时：默认开启；用 capture 抢点击，避免链接触发跳转。脚本追加在最后一个 </html> 前。 */
const PICK_RUNTIME = `(function(){
if(window.__c456PickReady)return;
window.__c456PickReady=true;
window.__c456PickOn=true;
var SRC='c456-brand-site-preview';
var hover=null,chipHover=null,sels=[],activeIdx=0,layer=null;
function ensureLayer(){
  if(layer)return layer;
  layer=document.createElement('div');
  layer.setAttribute('data-c456-pick-layer','1');
  layer.style.cssText='position:fixed;inset:0;pointer-events:none;z-index:2147483645;overflow:hidden';
  (document.documentElement||document.body).appendChild(layer);
  return layer;
}
function clearLayer(){
  var l=ensureLayer();
  while(l.firstChild)l.removeChild(l.firstChild);
}
function paintEl(el,color){
  if(!el||!el.getBoundingClientRect)return;
  var r=el.getBoundingClientRect();
  var b=document.createElement('div');
  b.setAttribute('data-c456-pick','1');
  b.style.cssText='position:fixed;pointer-events:none;border:2px solid '+(color||'#2563eb')+';background:rgba(37,99,235,.1);box-sizing:border-box;border-radius:2px';
  b.style.top=Math.max(0,r.top)+'px';
  b.style.left=Math.max(0,r.left)+'px';
  b.style.width=Math.max(0,r.width)+'px';
  b.style.height=Math.max(0,r.height)+'px';
  ensureLayer().appendChild(b);
}
function repaint(){
  clearLayer();
  for(var i=0;i<sels.length;i++)paintEl(sels[i],i===activeIdx?'#7c3aed':'#1d4ed8');
  if(hover&&sels.indexOf(hover)<0)paintEl(hover,'#2563eb');
  if(chipHover)paintEl(chipHover,'#f59e0b');
}
function cssPath(el){
  var parts=[],n=el,depth=0;
  while(n&&n.nodeType===1&&depth<6){
    var t=n.tagName.toLowerCase();
    if(n.id){parts.unshift(t+'#'+n.id);break;}
    var p=n.parentElement,ix=1;
    if(p){var sib=p.children;for(var i=0;i<sib.length;i++){if(sib[i]===n){ix=i+1;break;}}}
    parts.unshift(t+':nth-child('+ix+')');
    n=p;depth++;
  }
  return parts.join(' > ');
}
function itemPayload(el){
  var text=((el.innerText||el.textContent||'')+'').replace(/\\s+/g,' ').trim().slice(0,160);
  var html=((el.outerHTML||'')+'').slice(0,900);
  var r=el.getBoundingClientRect();
  return{tag:(el.tagName||'').toLowerCase(),text:text,path:cssPath(el),html:html,rect:{top:r.top,left:r.left,width:r.width,height:r.height}};
}
function postPick(){
  if(!sels.length)return;
  var items=[];
  for(var i=0;i<sels.length;i++)items.push(itemPayload(sels[i]));
  try{parent.postMessage({source:SRC,type:'pick',items:items,activeIndex:activeIdx},'*')}catch(x){}
}
function skip(el){
  return !el||el===document.documentElement||el===document.body||(el.getAttribute&&el.getAttribute('data-c456-pick'));
}
document.addEventListener('mouseover',function(e){
  if(!window.__c456PickOn)return;
  var t=e.target;if(t&&t.nodeType===3)t=t.parentElement;
  if(skip(t))return;
  hover=t;
  if(sels.indexOf(t)<0)repaint();
},true);
document.addEventListener('mouseout',function(e){
  if(!window.__c456PickOn)return;
  if(e.target===hover){hover=null;repaint();}
},true);
document.addEventListener('click',function(e){
  if(!window.__c456PickOn)return;
  e.preventDefault();
  e.stopPropagation();
  var t=e.target;if(t&&t.nodeType===3)t=t.parentElement;
  if(skip(t))return;
  var multi=e.metaKey||e.ctrlKey;
  var idx=sels.indexOf(t);
  if(multi){
    if(idx>=0){sels.splice(idx,1);activeIdx=Math.max(0,Math.min(activeIdx,sels.length-1));}
    else{sels.push(t);activeIdx=sels.length-1;}
  }else{
    sels=[t];activeIdx=0;
  }
  repaint();
  postPick();
},true);
document.addEventListener('keydown',function(e){
  if(!window.__c456PickOn)return;
  if(e.key==='Escape'){
    sels=[];activeIdx=0;hover=null;chipHover=null;
    clearLayer();
    try{parent.postMessage({source:SRC,type:'pick-clear'},'*')}catch(x){}
  }
},true);
window.addEventListener('message',function(e){
  var d=e.data;if(!d||d.source!==SRC)return;
  if(d.type==='pick-toggle'){
    window.__c456PickOn=!!d.enabled;
    if(!window.__c456PickOn){
      sels=[];activeIdx=0;hover=null;chipHover=null;
      clearLayer();
      try{parent.postMessage({source:SRC,type:'pick-clear'},'*')}catch(x){}
    }
  }
  if(d.type==='pick-clear'){
    sels=[];activeIdx=0;hover=null;chipHover=null;
    clearLayer();
  }
  if(d.type==='pick-hover'){
    chipHover=null;
    if(typeof d.path==='string'&&d.path){
      try{chipHover=document.querySelector(d.path);}catch(x){chipHover=null;}
    }
    repaint();
  }
  if(d.type==='pick-select-path'&&typeof d.path==='string'){
    var el=null;
    try{el=document.querySelector(d.path);}catch(x){el=null;}
    if(skip(el))return;
    sels=[el];activeIdx=0;
    repaint();
    postPick();
  }
  if(d.type==='pick-sync'&&Array.isArray(d.paths)){
    sels=[];activeIdx=0;
    for(var pi=0;pi<d.paths.length;pi++){
      try{var pel=document.querySelector(d.paths[pi]);if(!skip(pel))sels.push(pel);}catch(x){}
    }
    if(sels.length){activeIdx=sels.length-1;repaint();postPick();}
    else{clearLayer();try{parent.postMessage({source:SRC,type:'pick-clear'},'*')}catch(x){}}
  }
  if(d.type==='apply-html'&&typeof d.html==='string'){
    var sx=window.scrollX||0,sy=window.scrollY||0;
    if(typeof Idiomorph==='undefined'||!Idiomorph.morph)return;
    var next=new DOMParser().parseFromString(d.html,'text/html');
    if(!next.documentElement)return;
    var MARK='data-c456-pick-runtime';
    Idiomorph.morph(document.documentElement,next.documentElement,{
      morphStyle:'outerHTML',
      head:{style:'morph'},
      callbacks:{
        beforeNodeRemoved:function(node){
          if(node&&node.nodeType===1&&node.tagName==='SCRIPT'&&node.hasAttribute&&node.hasAttribute(MARK))return false;
          return true;
        },
        beforeNodeAdded:function(node){
          if(node&&node.nodeType===1&&node.tagName==='SCRIPT'&&node.hasAttribute&&node.hasAttribute(MARK)){
            if(document.querySelector('script['+MARK+']'))return false;
          }
          return true;
        }
      }
    });
    box=null;hover=null;chipHover=null;sels=[];activeIdx=0;layer=null;
    window.scrollTo(sx,sy);
  }
});
})();`;

function withAttr(tag: string, name: string, value: string): string {
  if (new RegExp(`\\s${name}=`, "i").test(tag)) return tag;
  return tag.replace(/>$/, ` ${name}="${value}">`);
}

/** 内容指纹（调试/测试用）。预览 iframe 已改 Idiomorph 同页更新，不再用此值当 remount key。 */
export function previewSrcDocKey(html: string): string {
  const len = html.length;
  if (len === 0) return "0:0";
  let hash = 0;
  const step = Math.max(1, Math.floor(len / 64));
  for (let i = 0; i < len; i += step) {
    hash = (Math.imul(hash, 31) + html.charCodeAt(i)) | 0;
  }
  hash = (Math.imul(hash, 31) + html.charCodeAt(len - 1)) | 0;
  return `${len}:${hash}`;
}

const PICK_MARKER = "data-c456-pick-runtime";

function stripPickRuntime(html: string): string {
  return html.replace(
    new RegExp(
      `<script[^>]*${PICK_MARKER}[^>]*>[\\s\\S]*?<\\/script>`,
      "gi",
    ),
    "",
  );
}

function appendPickRuntime(html: string): string {
  const cleaned = stripPickRuntime(html);
  // 避免字面 </script> 截断：用拼接闭合标签
  const close = "</" + "script>";
  // Idiomorph 与点选/帧内 morph 同脚本：sandbox 无 allow-same-origin 时父页读不到 contentDocument
  const tag = `<script ${PICK_MARKER}="1">${idiomorphMin};\n${PICK_RUNTIME}${close}`;
  const idx = cleaned.toLowerCase().lastIndexOf("</html>");
  if (idx >= 0) {
    return `${cleaned.slice(0, idx)}${tag}${cleaned.slice(idx)}`;
  }
  return `${cleaned}${tag}`;
}

/** 用 html 上的 onclick 拦跳转；点选脚本挂在最后一个 </html> 前，不切第一个 </body>。 */
export function injectPreviewBridge(html: string, opts?: { pick?: boolean }): string {
  const pick = opts?.pick !== false;
  let source = (html || "").replace(/\sdata-c456-preview-bridge="[^"]*"/gi, "");
  source = stripPickRuntime(source);

  if (/<html\b/i.test(source)) {
    source = source.replace(/<html\b[^>]*>/i, (tag) =>
      withAttr(withAttr(tag, "onclick", CLICK_HANDLER), "onsubmit", SUBMIT_HANDLER),
    );
  } else {
    source = `<html onclick="${CLICK_HANDLER}" onsubmit="${SUBMIT_HANDLER}"><body>${source}</body></html>`;
  }

  return pick ? appendPickRuntime(source) : source;
}

export function missingPreviewPageHtml(path: string): string {
  const safe = path.replace(/[<>&"]/g, "");
  return `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="UTF-8"><title>页面不存在</title></head><body style="font-family:system-ui,sans-serif;padding:2.5rem;color:#444">
<p>这一页还没有生成。</p>
<p style="color:#888">${safe}</p>
<p><a href="/">回到首页</a></p>
</body></html>`;
}

export function isPreviewNavMessage(
  data: unknown,
): data is { source: string; type?: string; href: string } {
  if (!data || typeof data !== "object") return false;
  const record = data as { source?: unknown; type?: unknown; href?: unknown };
  if (record.source !== BRAND_SITE_PREVIEW_MESSAGE || typeof record.href !== "string") {
    return false;
  }
  // 兼容旧消息（无 type）与 type:'nav'
  return record.type == null || record.type === "nav";
}

export function isPreviewPickMessage(data: unknown): data is PreviewPickPayload {
  if (!data || typeof data !== "object") return false;
  const record = data as PreviewPickPayload & Partial<PreviewPickItem>;
  if (record.source !== BRAND_SITE_PREVIEW_MESSAGE || record.type !== "pick") return false;
  if (Array.isArray(record.items) && record.items.length > 0) {
    const first = record.items[0];
    return (
      typeof first?.path === "string" &&
      !!first?.rect &&
      typeof first.rect.top === "number"
    );
  }
  // 兼容旧版单条 pick
  return (
    typeof record.path === "string" &&
    !!record.rect &&
    typeof record.rect.top === "number"
  );
}

/** 统一成多选 pick 载荷。 */
export function normalizePreviewPickPayload(data: unknown): PreviewPickPayload | null {
  if (!isPreviewPickMessage(data)) return null;
  const record = data as PreviewPickPayload & Partial<PreviewPickItem>;
  if (Array.isArray(record.items) && record.items.length > 0) {
    return {
      source: BRAND_SITE_PREVIEW_MESSAGE,
      type: "pick",
      items: record.items,
      activeIndex:
        typeof record.activeIndex === "number"
          ? Math.max(0, Math.min(record.activeIndex, record.items.length - 1))
          : record.items.length - 1,
    };
  }
  return {
    source: BRAND_SITE_PREVIEW_MESSAGE,
    type: "pick",
    items: [
      {
        tag: String(record.tag ?? "div"),
        text: String(record.text ?? ""),
        path: String(record.path ?? ""),
        html: String(record.html ?? ""),
        rect: record.rect!,
      },
    ],
    activeIndex: 0,
  };
}

export function isPreviewPickClearMessage(
  data: unknown,
): data is { source: string; type: "pick-clear" } {
  if (!data || typeof data !== "object") return false;
  const record = data as { source?: unknown; type?: unknown };
  return record.source === BRAND_SITE_PREVIEW_MESSAGE && record.type === "pick-clear";
}

/** 从 cssPath 片段取标签名，如 `div:nth-child(2)` → `div`。 */
export function pickPathSegmentLabel(segment: string): string {
  const match = segment.trim().match(/^([a-z][a-z0-9-]*)/i);
  return match?.[1]?.toLowerCase() ?? segment.trim();
}

/** 点选路径面包屑：逐级前缀，便于点上级区域。 */
export function pickPathBreadcrumbs(fullPath: string): Array<{ label: string; path: string }> {
  const segments = fullPath
    .split(/\s*>\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const crumbs: Array<{ label: string; path: string }> = [];
  for (let index = 0; index < segments.length; index += 1) {
    crumbs.push({
      label: pickPathSegmentLabel(segments[index]!),
      path: segments.slice(0, index + 1).join(" > "),
    });
  }
  return crumbs;
}

/** 点选浮层定位：优先元素下方，不够则翻到上方，并钳在预览画布内。 */
export function clampPickPopoverPosition(input: {
  stageWidth: number;
  stageHeight: number;
  iframeOffsetTop: number;
  iframeOffsetLeft: number;
  rect: { top: number; left: number; width: number; height: number };
  popoverWidth?: number;
  popoverHeight?: number;
  margin?: number;
}): { top: number; left: number; placed: "below" | "above" } {
  const margin = input.margin ?? 8;
  const popoverW = Math.min(input.popoverWidth ?? 320, Math.max(120, input.stageWidth - margin * 2));
  const popoverH = input.popoverHeight ?? 140;
  const absTop = input.iframeOffsetTop + input.rect.top;
  const absLeft = input.iframeOffsetLeft + input.rect.left;
  const absBottom = absTop + input.rect.height;

  let placed: "below" | "above" = "below";
  let top = absBottom + margin;
  if (top + popoverH > input.stageHeight - margin) {
    top = absTop - popoverH - margin;
    placed = "above";
  }
  const maxTop = Math.max(margin, input.stageHeight - popoverH - margin);
  top = Math.min(Math.max(margin, top), maxTop);

  // 相对选中元素水平居中，再钳进画布
  const elementCenterX = absLeft + input.rect.width / 2;
  let left = elementCenterX - popoverW / 2;
  const maxLeft = Math.max(margin, input.stageWidth - popoverW - margin);
  left = Math.min(Math.max(margin, left), maxLeft);

  return { top, left, placed };
}

export const PICK_EDIT_MARKER_START = "<!--c456-pick-edit-->";
export const PICK_EDIT_MARKER_END = "<!--/c456-pick-edit-->";

export type PickEditChatDisplay = {
  pageTitle: string;
  pagePath: string;
  tag: string;
  pickCount: number;
  userRequest: string;
};

function oneLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function envelopeField(name: string, value: string): string {
  return `${name}: ${oneLine(value)}`;
}

function fieldFromEnvelope(block: string, name: string): string | null {
  const prefix = `${name}:`;
  for (const rawLine of block.split("\n")) {
    const line = rawLine.trim();
    if (line.startsWith(prefix)) return line.slice(prefix.length).trim();
  }
  return null;
}

function userRequestFromPrompt(body: string): string | null {
  for (const marker of ["\n需求：", "\n用户需求："]) {
    const idx = body.lastIndexOf(marker);
    if (idx >= 0) return body.slice(idx + marker.length).trim() || null;
  }
  if (body.startsWith("需求：")) return body.slice("需求：".length).trim() || null;
  if (body.startsWith("用户需求：")) return body.slice("用户需求：".length).trim() || null;
  return null;
}

function parseEnvelopePickEdit(text: string): PickEditChatDisplay | null {
  if (!text.startsWith(PICK_EDIT_MARKER_START)) return null;
  const endIdx = text.indexOf(PICK_EDIT_MARKER_END);
  if (endIdx < 0) return null;
  const block = text.slice(PICK_EDIT_MARKER_START.length, endIdx);
  const slug = fieldFromEnvelope(block, "slug");
  const titleRaw = fieldFromEnvelope(block, "title");
  const pageTitle = oneLine(titleRaw ?? "") || slug || "当前页";
  const pagePath = fieldFromEnvelope(block, "path") || "/";
  const tag = fieldFromEnvelope(block, "tag") || "div";
  const countRaw = fieldFromEnvelope(block, "count");
  const userRequest = userRequestFromPrompt(text.slice(endIdx + PICK_EDIT_MARKER_END.length));
  if (!userRequest) return null;
  const pickCount = countRaw ? Number.parseInt(countRaw, 10) : 1;
  return {
    pageTitle,
    pagePath,
    tag,
    pickCount: Number.isFinite(pickCount) && pickCount > 0 ? pickCount : 1,
    userRequest,
  };
}

function parseLegacyPickEdit(text: string): PickEditChatDisplay | null {
  const header = text.match(/^【点选改页 · (.+?) · (.+?)】/);
  if (!header) return null;
  const tagMatch = text.match(/^选中元素：<([^>\n]+)>/m);
  const userRequest = userRequestFromPrompt(text);
  if (!tagMatch || !userRequest) return null;
  return {
    pageTitle: header[1].trim(),
    pagePath: header[2].trim(),
    tag: tagMatch[1].trim(),
    pickCount: 1,
    userRequest,
  };
}

/** 聊天气泡用：信封优先；旧版【点选改页】头也可识别，避免把普通对话当指令。 */
export function parsePickEditChatDisplay(content: string): PickEditChatDisplay | null {
  const text = (content ?? "").trim();
  return parseEnvelopePickEdit(text) ?? parseLegacyPickEdit(text);
}

/** 商户可见的指令条，不含路径 / HTML / 工具名。 */
export function pickEditChipLabel(display: PickEditChatDisplay): string {
  const suffix =
    display.pickCount > 1 ? ` · ${display.pickCount} 处` : ` · <${display.tag}>`;
  return `点选改页 · ${display.pageTitle}${suffix}`;
}

/** 组装发给 Hermes 的点选改页提示（仍走整页 write_page）。 */
export function buildPickEditPrompt(input: {
  userRequest: string;
  pageSlug: string;
  pageTitle?: string | null;
  pagePath: string;
  pick: Pick<{ tag: string; text: string; path: string; html: string }, "tag" | "text" | "path" | "html">;
}): string {
  const title = oneLine(input.pageTitle ?? "") || input.pageSlug || "当前页";
  const text = input.pick.text.trim() || "（无可见文字）";
  const tag = oneLine(input.pick.tag) || "div";
  return [
    PICK_EDIT_MARKER_START,
    envelopeField("slug", input.pageSlug),
    envelopeField("title", title),
    envelopeField("path", input.pagePath || "/"),
    envelopeField("tag", tag),
    envelopeField("count", "1"),
    PICK_EDIT_MARKER_END,
    "",
    `@1 <${tag}> ${text} · ${input.pick.path}`,
    "",
    `上下文：slug=${input.pageSlug}；可 read_page 查阅或 execute_edit 改页（按用户需求选择）`,
    "",
    `需求：${input.userRequest.trim()}`,
  ].join("\n");
}

/** 栏目条目正文点选：改 settings.body 后 write_item */
export function buildCollectionItemPickEditPrompt(input: {
  userRequest: string;
  collectionKey: string;
  itemSlug: string;
  itemTitle?: string | null;
  itemPath: string;
  fieldId: string;
  currentBodyHtml: string;
  currentSettings: Record<string, string>;
  pick: Pick<{ tag: string; text: string; path: string; html: string }, "tag" | "text" | "path" | "html">;
}): string {
  const title = oneLine(input.itemTitle ?? "") || input.itemSlug;
  const snippet = input.pick.html.trim().slice(0, 700);
  const text = input.pick.text.trim() || "（无可见文字）";
  const tag = oneLine(input.pick.tag) || "div";
  const bodyDraft = input.currentBodyHtml.trim().slice(0, 8000);
  const settingsHint = JSON.stringify(input.currentSettings).slice(0, 2000);
  return [
    PICK_EDIT_MARKER_START,
    envelopeField("title", title),
    envelopeField("path", input.itemPath),
    envelopeField("tag", tag),
    PICK_EDIT_MARKER_END,
    "",
    `【点选改页 · ${title} · ${input.itemPath}】`,
    `选中元素：<${tag}>`,
    `可见文字：${text}`,
    `大致路径：${input.pick.path}`,
    "HTML 片段：",
    "```html",
    snippet,
    "```",
    "",
    `这是栏目条目（collection_key=${input.collectionKey}，slug=${input.itemSlug}）的正文字段 ${input.fieldId}。`,
    "请按用户需求只改正文中该处（可连带周边排版），再 brand_site_write_item 提交完整 settings（须含改后的全文 body 及其它字段，勿只传片段）。",
    "禁止整篇重写无关段落。",
    "",
    "当前 settings 摘要：",
    "```json",
    settingsHint,
    "```",
    "",
    "当前 body HTML（底稿）：",
    "```html",
    bodyDraft,
    "```",
    "",
    `用户需求：${input.userRequest.trim()}`,
  ].join("\n");
}
